import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import { pageHeaderHeight } from 'components/PageHeader';
import { useCasesViewMode } from 'components/StudyDashboard/ProceduresPage/useCasesViewMode';
import { includes } from 'es-toolkit/compat';
import { Permission } from 'interfaces/permissionOption';
import { usePermissions } from 'utils/usePermissions';

export const FlexBody = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : undefined,
}));

export const annotationsDrawerWidth = 500;
export const filtersDrawerWidth = 320;
export const overviewDrawerWidth = 320;
export const jobDrawerWidth = 400;
export const settingsDetailsDrawerWidth = 400;
export const studyDashboardHeaderHeight = 88;
export const studyDashboardTabsHeight = 48;
export const caseToolbarHeight = 48;
export const editDrawerBottomPadding = 75;
export const studyDashboardLayoutTransitionDurationMs = 120;

export const createStudyDashboardLayoutTransition = (theme: Theme, easing: string) =>
  theme.transitions.create(['margin', 'width'], {
    easing,
    duration: studyDashboardLayoutTransitionDurationMs,
  });

export interface MainProps {
  leftDrawerOpen?: boolean;
  rightDrawerOpen?: boolean;
  rightDrawerWidth?: number;
  paddingBottom?: number | string;
  marginBottom?: number | string;
  useOwnScroll?: boolean;
}
const customProps: Array<keyof MainProps> = [
  'leftDrawerOpen',
  'rightDrawerOpen',
  'rightDrawerWidth',
  'marginBottom',
  'paddingBottom',
  'useOwnScroll',
];

export const Main = styled('main', {
  shouldForwardProp: (prop) => !includes(customProps, prop),
})<MainProps>(
  ({
    theme,
    leftDrawerOpen,
    rightDrawerOpen,
    paddingBottom,
    marginBottom,
    useOwnScroll = true,
    rightDrawerWidth = overviewDrawerWidth,
  }) => ({
    transition: createStudyDashboardLayoutTransition(theme, theme.transitions.easing.sharp),
    display: 'flex',
    flexDirection: 'column',
    overflowY: useOwnScroll ? 'auto' : 'visible',
    width: '100%',
    height: useOwnScroll ? '100%' : 'auto',
    minHeight: '100%',
    ...(leftDrawerOpen && {
      marginLeft: `${rightDrawerWidth}px`,
      width: `calc(100% - ${rightDrawerWidth}px)`,
      transition: createStudyDashboardLayoutTransition(theme, theme.transitions.easing.easeOut),
    }),
    ...(rightDrawerOpen && {
      width: `calc(100% - ${rightDrawerWidth}px)`,
      transition: createStudyDashboardLayoutTransition(theme, theme.transitions.easing.easeOut),
    }),
    ...(leftDrawerOpen &&
      rightDrawerOpen && {
        width: `calc(100% - ${rightDrawerWidth}px - ${rightDrawerWidth}px)`,
      }),
    ...(Boolean(paddingBottom) && { paddingBottom }),
    ...(Boolean(marginBottom) && { marginBottom }),
  })
);

export const useStudyDashboardStyles = ({
  reserveBottomDrawerSpace = true,
}: {
  reserveBottomDrawerSpace?: boolean;
} = {}) => {
  const { hasPermission } = usePermissions();
  const displayTabs = hasPermission(Permission.ViewAnnotationsList);
  const borderHeight = 1;

  const { casesViewMode } = useCasesViewMode();

  const baseMarginBottom = reserveBottomDrawerSpace && casesViewMode !== 'table' ? editDrawerBottomPadding : 0;
  const additionalDrawerPaddingBottom =
    reserveBottomDrawerSpace && casesViewMode === 'table' ? editDrawerBottomPadding : 0;

  return {
    mainMarginTop:
      studyDashboardHeaderHeight + pageHeaderHeight + borderHeight + (displayTabs ? studyDashboardTabsHeight : 0),
    mainMarginBottom: baseMarginBottom,
    additionalDrawerPaddingBottom,
  };
};
